import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Box, Typography, Grid } from "@mui/material";
import { useEffect, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { createUser } from "@/redux/user/user.action";
import { allUsers as fetchAllUsers } from "@/redux/user/user.action";
import { SuccessToast } from "@/utilities/toasts/SuccessToast";
import { ErrorToast } from "@/utilities/toasts/ErrorToast";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { IconX } from "@tabler/icons-react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useSelector } from "react-redux";
import { userSelector } from "@/redux/user/user.selector";
import { SubsSelector } from "./SubsSelector";
import { getCompanies } from "@/redux/clients/clients.action";
import { clientSelector } from "@/redux/clients/clients.selector";
import { Client } from "@/redux/clients/types/Clients.interface";
import { Badge } from "./ui/badge";
import { getRoles } from "@/enum/Roles";

const formSchema = z.object({
  username: z
    .string({ message: "Correo inválido." })
    .min(8, { message: "Usuario debe ser de al menos 8 letras." }),
  email: z.string().email({ message: "Correo inválido." }),
  password: z.string().min(8, {
    message: "Contraseña debe ser de al menos 8 letras.",
  }),
  role: z.string({
    required_error: "Rol es requerido.",
  }),
  company: z.string().min(1, { message: "Empresa es requerida." }),
  position: z.string().min(1, { message: "Cargo es requerido." }),
});

interface NewUserFormProps {
  isNewUserFormOpen: boolean;
  setIsNewUserFormOpen: (open: boolean) => void;
}

export const NewUserForm = ({
  isNewUserFormOpen,
  setIsNewUserFormOpen,
}: NewUserFormProps) => {
  const { userInfo } = useSelector(userSelector).ui;
  const roles = userInfo && getRoles(userInfo);
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useAppDispatch();
  const { companies } = useSelector(clientSelector).ui;
  const [selectedCompanies, setSelectedCompanies] = useState<Client[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  useEffect(() => {
    dispatch(getCompanies());
  }, [dispatch]);

  const findCompanyDetails = (companyId: string) => {
    const companyDetails = companies?.find((company) => {
      return company.id.toString() === companyId;
    });
    if (!companyDetails) return null;
    return companyDetails;
  };

  const handleSelectCompany = (companyId: string) => {
    const companyDetails = findCompanyDetails(companyId);
    if (!companyDetails) return;

    setSelectedCompanies((prev) => {
      const alreadyExists = prev.some((c) => c.id === companyDetails.id);
      if (alreadyExists) {
        return prev;
      }
      return [...prev, companyDetails];
    });
  };

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: "",
      email: "",
      password: "",
      role: "",
      company: "",
      position: "",
    },
  });

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    const listedCompanies = selectedCompanies.map((company) => {
      return {
        id: company.id,
        folders: company.carpetas.map((carpeta) => carpeta.id.toString()),
      };
    });
    // Convert listedCompanies to match the Subscriptions type which requires cliente property
    const formattedSubscriptions = listedCompanies.map((company) => ({
      id: company.id,
      folders: company.folders,
      cliente: company.id.toString(), // Adding the required cliente property
    }));

    const newUser = {
      ...values,
      name: values.username,
      role: values.role,
      suscripciones: formattedSubscriptions,
    };

    const formattedNewUser = {
      ...newUser,
      suscripciones: newUser.suscripciones.map((sub) => ({
        ...sub,
        cliente: {
          id: parseInt(sub.cliente),
          nombre: "",
          numeroCliente: "",
        },
      })),
    };

    const response = await dispatch(createUser(formattedNewUser));
    if (response.payload) {
      await dispatch(fetchAllUsers());
      SuccessToast({ message: "Usuario creado correctamente" });
      setIsNewUserFormOpen(false);
      form.reset();
      setSelectedCompanies([]);
      setSearchTerm("");
    } else {
      ErrorToast({ message: "Error al crear el usuario" });
      form.reset();
      setSelectedCompanies([]);
      setSearchTerm("");
      setIsNewUserFormOpen(false);
    }
  };

  const handleRemoveCompany = (companyId: number) => {
    setSelectedCompanies((prev) =>
      prev.filter((company) => company.id !== companyId),
    );
  };

  return (
    <>
      <Dialog
        open={isNewUserFormOpen}
        onOpenChange={(open) => {
          setIsNewUserFormOpen(!isNewUserFormOpen);
          form.reset();
          setSelectedCompanies([]);
          setSearchTerm("");
        }}
      >
        <VisuallyHidden>
          <DialogTitle>UserConfigs</DialogTitle>
        </VisuallyHidden>
        <DialogContent
          className="bg-sidebar min-w-[15rem] w-[30rem]"
          aria-describedby={undefined}
        >
          <DialogHeader className="flex pb-0 h-fit gap-1">
            <Box className="flex justify-end w-full font-bold">
              <Button
                className="p-0 text-[#1F6B45] h-fit"
                onClick={() => {
                  setIsNewUserFormOpen(false);
                  form.reset();
                  setSelectedCompanies([]);
                  setSearchTerm("");
                }}
                aria-label="Close"
              >
                <IconX className="p-0" />
              </Button>
            </Box>
            <DialogTitle className="flex flex-col gap-1">
              <Typography
                fontFamily={"Inter"}
                fontSize={"18px"}
                fontWeight={500}
              >
                Configuración de usuario
              </Typography>
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-500">
              Consulta y edita la información del perfil del usuario
              seleccionado.
            </DialogDescription>
          </DialogHeader>
          <Box className="flex flex-col gap-1">
            <Form {...form}>
              <form
                className="flex flex-col justify-between"
                onSubmit={form.handleSubmit((values) => {
                  handleSubmit(values);
                })}
              >
                <Grid container className="w-full justify-between">
                  <Grid item xs={12}>
                    <FormField
                      control={form.control}
                      name="username"
                      render={({ field }) => (
                        <FormItem className="w-full flex flex-col items-center justify-start">
                          <FormLabel className="flex items-center justify-start w-full">
                            <Typography
                              fontFamily={"Inter"}
                              fontSize={"14px"}
                              fontWeight={500}
                              letterSpacing={0}
                            >
                              Usuario:
                            </Typography>
                          </FormLabel>
                          <Box className="flex flex-col w-full h-[3.4rem] mt-1">
                            <FormControl>
                              <Input
                                type="text"
                                placeholder="Ingresa el nombre de usuario"
                                {...field}
                                className="text-[12px]"
                              />
                            </FormControl>
                            <FormMessage className="pt-1" />
                          </Box>
                        </FormItem>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem className="w-full flex items-center flex-col justify-start">
                          <FormLabel className="flex items-center justify-start w-full">
                            <Typography
                              fontFamily={"Inter"}
                              fontSize={"14px"}
                              fontWeight={500}
                              letterSpacing={0}
                            >
                              E-mail:
                            </Typography>
                          </FormLabel>
                          <Box className="flex flex-col w-full h-[3.4rem]">
                            <FormControl>
                              <Input
                                type="email"
                                placeholder="Ingresa el e-mail"
                                {...field}
                                className="text-[12px]"
                              />
                            </FormControl>
                            <FormMessage className="pt-1 text-[10px]" />
                          </Box>
                        </FormItem>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem className="w-full flex items-center flex-col justify-start">
                          <FormLabel className="flex items-center justify-start w-full">
                            <Typography
                              fontFamily={"Inter"}
                              fontSize={"14px"}
                              fontWeight={500}
                              letterSpacing={0}
                            >
                              Contraseña:
                            </Typography>
                          </FormLabel>
                          <Box className="flex flex-col w-full h-[3.4rem]">
                            <FormControl>
                              <div className="relative w-full">
                                <Input
                                  {...field}
                                  type={showPassword ? "text" : "password"}
                                  placeholder="Ingresa la contraseña"
                                  className="text-[12px]"
                                />
                                <button
                                  type="button"
                                  onClick={() =>
                                    setShowPassword((prev) => !prev)
                                  }
                                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                                  tabIndex={-1}
                                >
                                  {showPassword ? (
                                    <EyeOff className="size-5 text-[#1F6B45]" />
                                  ) : (
                                    <Eye className="size-5 text-[#1F6B45]" />
                                  )}
                                </button>
                              </div>
                            </FormControl>
                            <FormMessage className="pt-1 text-[10px]" />
                          </Box>
                        </FormItem>
                      )}
                    />
                  </Grid>
                  <Grid item xs={5.5}>
                    <FormField
                      control={form.control}
                      name="role"
                      render={({ field }) => (
                        <FormItem className="w-full flex items-center flex-col justify-start">
                          <FormLabel className="flex items-center justify-start w-full">
                            <Typography
                              fontFamily={"Inter"}
                              fontSize={"14px"}
                              fontWeight={500}
                              letterSpacing={0}
                            >
                              Rol:
                            </Typography>
                          </FormLabel>
                          <Select onValueChange={field.onChange}>
                            <FormControl>
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Selecciona un rol" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectGroup>
                                <SelectLabel>
                                  <Typography
                                    fontFamily={"Inter"}
                                    fontSize={"12px"}
                                    fontWeight={400}
                                    className="text-gray-500"
                                  >
                                    Roles
                                  </Typography>
                                </SelectLabel>
                                {roles &&
                                  roles?.map(
                                    (role: {
                                      value: string;
                                      label: string;
                                      visibleIfAdmin: boolean;
                                    }) =>
                                      role.visibleIfAdmin && (
                                        <SelectItem
                                          key={role.value}
                                          value={role.value}
                                        >
                                          <Typography
                                            fontFamily={"Inter"}
                                            fontSize={"14px"}
                                            fontWeight={400}
                                          >
                                            {role.label}
                                          </Typography>
                                        </SelectItem>
                                      ),
                                  )}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />
                  </Grid>
                  <Grid item xs={5.5}>
                    <FormField
                      control={form.control}
                      name="company"
                      render={({ field }) => (
                        <FormItem className="w-full flex items-center flex-col justify-start">
                          <FormLabel className="flex items-center justify-start w-full">
                            <Typography
                              fontFamily={"Inter"}
                              fontSize={"14px"}
                              fontWeight={500}
                              letterSpacing={0}
                            >
                              Compañía:
                            </Typography>
                          </FormLabel>
                          <Box className="flex flex-col w-full h-[3.4rem] mt-1">
                            <FormControl>
                              <Input
                                {...field}
                                placeholder="Ingresa la compañía"
                                className="text-[12px]"
                              />
                            </FormControl>
                            <FormMessage className="pt-1 text-[10px]" />
                          </Box>
                        </FormItem>
                      )}
                    />
                  </Grid>
                  <Grid item xs={5.5}>
                    <FormField
                      control={form.control}
                      name="position"
                      render={({ field }) => (
                        <FormItem className="w-full flex items-center flex-col justify-start">
                          <FormLabel className="flex items-center justify-start w-full">
                            <Typography
                              fontFamily={"Inter"}
                              fontSize={"14px"}
                              fontWeight={500}
                              letterSpacing={0}
                            >
                              Cargo:
                            </Typography>
                          </FormLabel>
                          <Box className="flex flex-col w-full h-[3.4rem]">
                            <FormControl>
                              <Input
                                {...field}
                                placeholder="Ingresa el cargo"
                                className="text-[12px]"
                              />
                            </FormControl>
                            <FormMessage className="pt-1 text-[10px]" />
                          </Box>
                        </FormItem>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      fontFamily={"Inter"}
                      fontSize={"14px"}
                      fontWeight={500}
                      letterSpacing={0}
                    >
                      Suscripción:
                    </Typography>
                    {form.watch("role") !== "Administrador" && (
                      <Input
                        type="text"
                        placeholder="Buscar compañía..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="text-[12px] mt-2 mb-2"
                      />
                    )}
                    <Box className="flex flex-col w-full min-h-[7rem]">
                      <Box className="flex flex-wrap gap-1 w-full max-h-[5rem] overflow-y-auto my-2">
                        {form.watch("role") !== "Administrador" ? (
                          selectedCompanies.length === 0 ? (
                            <Box>
                              <Typography
                                fontFamily={"Inter"}
                                fontSize={"12px"}
                                fontWeight={400}
                                className="mb-2"
                              >
                                No hay compañias a las que está suscrito el
                                usuario
                              </Typography>
                            </Box>
                          ) : (
                            selectedCompanies.map((subscription) => {
                              return (
                                <Badge
                                  key={subscription.id}
                                  asChild
                                  variant="default"
                                  className="shadow-sm hover:bg-[#1F6B45] text-[#1F6B45] hover:text-white h-fit"
                                >
                                  <span>
                                    <Typography
                                      fontFamily={"Inter"}
                                      fontSize={"10px"}
                                      fontWeight={400}
                                      key={subscription.id}
                                      textTransform={"capitalize"}
                                      className="max-w-[100px] truncate"
                                    >
                                      {subscription.nombre}
                                    </Typography>
                                    <span className="rounded-full p-0.5 hover:bg-white/30">
                                      <IconX
                                        className="w-3 h-3 cursor-pointer text-inherit"
                                        onClick={() =>
                                          handleRemoveCompany(subscription.id)
                                        }
                                      />
                                    </span>
                                  </span>
                                </Badge>
                              );
                            })
                          )
                        ) : (
                          <Typography
                            fontFamily={"Inter"}
                            fontSize={"12px"}
                            fontWeight={400}
                          >
                            Este usuario está suscrito a todas las compañias
                          </Typography>
                        )}
                      </Box>
                      {form.watch("role") !== "Administrador" && (
                        <SubsSelector
                          companies={companies || []}
                          selectedCompanies={selectedCompanies}
                          onChange={handleSelectCompany}
                          searchTerm={searchTerm}
                        />
                      )}
                    </Box>
                  </Grid>
                </Grid>
                <DialogFooter className="flex justify-end sm:pt-[1.5rem]">
                  <Button variant="contained" type="submit">
                    Crear usuario
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </Box>

          {/* <DialogFooter
            className={`flex w-full gap-4 pt-4 flex-row items-center justify-center flex-wrap sm:flex-row sm:justify-end sm:items-center`}
          >
            {editVariable && (
              <Button
                variant="cancel"
                className="w-full justify-self-start w-[7.5rem] flex"
                onClick={() => handleEditVariable(false)}
              >
                <Typography
                  fontFamily={"Inter"}
                  fontSize={"14px"}
                  fontWeight={500}
                >
                  Cancelar
                </Typography>
              </Button>
            )}
            {userInfo.role === "Administrador" && (
              <Button
                variant="contained"
                className="w-full justify-self-end w-[7.5rem] flex hover:bg-[#1F6B45]/90 shadow-sm"
                onClick={() =>
                  editVariable
                    ? handleEditVariableChange()
                    : handleEditVariable(true)
                }
              >
                <Typography
                  fontFamily={"Inter"}
                  fontSize={"14px"}
                  fontWeight={500}
                >
                  {editVariable ? "Aceptar" : "Editar"}
                </Typography>
              </Button>
            )}
          </DialogFooter> */}
        </DialogContent>
      </Dialog>
    </>
  );
};
